import Pause from '@mui/icons-material/Pause';
import PlayArrow from '@mui/icons-material/PlayArrow';
import VolumeMute from '@mui/icons-material/VolumeMute';
import VolumeOff from '@mui/icons-material/VolumeOff';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import ImageWrapper from '../ImageWrapper';
import { IYouTubeVideoEmbed, OnStateChangeEvent, Player, PlayerEvent, PlayerOptions } from './YouTubeVideoEmbed.helpers';
import { ytImageStyles, ytVideoInnerSectionStyles, ytVideoSectionWrapperStyles, videoControlIconStyles, ytVideoControlStyles, youtubeMobileStyles, youtubeIframeStyles, videoHoverEffect } from './YouTubeVideoEmbed.styles';
import { videoIconButtonStyles } from '@shared-components/components/VideoContent/VideoContent.styles';
declare global {
  interface Window {
    onYouTubeIframeAPIReady: () => void;
    YT: {
      Player: new (elementId: string, options: PlayerOptions) => Player;
      PlayerState: {
        ENDED: number;
      };
    };
  }
}
export default function YouTubeVideoEmbed({
  youTubeVideoId,
  youTubeVideoImage,
  isBorderRadius = true,
  autoPlay = false,
  isLooped = false,
  isMuted = false,
  fromHero = false,
  videoControlsSx,
  sixteenByNineVideoRatio = false
}: Readonly<IYouTubeVideoEmbed>) {
  const [isPlaying, setIsPlaying] = useState<boolean | null>(autoPlay);
  const [iframeSrc, setIframeSrc] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isNowMuted, setIsNowMuted] = useState<boolean | null>(autoPlay || isMuted);
  const [isVideoHovered, setIsVideoHovered] = useState<boolean>(false);
  const [isFirstPlayTriggered, setIsFirstPlayTriggered] = useState<boolean>(false);
  const theme = useTheme();
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const hasYouTubePlayer = () => Boolean(globalThis.window?.YT?.Player);
  const waitForYouTubePlayer = (resolve: () => void) => {
    const checkYTPlayer = setInterval(() => {
      if (hasYouTubePlayer()) {
        clearInterval(checkYTPlayer);
        resolve();
      }
    }, 100);
  };
  const loadYouTubeIframeAPI = async (youTubeVideoId: string) => {
    return await new Promise<void>((resolve, reject) => {
      if (hasYouTubePlayer()) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.id = `youtube_id_${youTubeVideoId}_${crypto.randomUUID()}`;
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      script.onload = () => {
        waitForYouTubePlayer(resolve);
      };
      script.onerror = error => {
        console.warn('YouTube API script failed to load.', error);
        reject(new Error('YouTube API script failed to load.'));
      };
      document.body.appendChild(script);
    });
  };
  type BuildYouTubePlayerParams = {
    uniqueId: string;
    youTubeVideoId: string;
    autoPlay: boolean;
    isLooped: boolean;
    isMuted: boolean;
    onReady: (event: PlayerEvent) => void;
    onStateChange: (event: OnStateChangeEvent) => void;
    playerRef: React.MutableRefObject<Player | null>;
  };
  const buildYouTubePlayer = async ({
    uniqueId,
    youTubeVideoId,
    autoPlay,
    isLooped,
    isMuted,
    onReady,
    onStateChange,
    playerRef
  }: BuildYouTubePlayerParams) => {
    return await new Promise<void>((resolve, reject) => {
      if (!hasYouTubePlayer()) {
        reject(new Error('YouTube API is not available.'));
        return;
      }
      playerRef.current = new globalThis.window.YT.Player(uniqueId, {
        videoId: youTubeVideoId,
        playerVars: {
          autoplay: autoPlay ? 1 : 0,
          loop: isLooped ? 1 : 0,
          mute: isMuted || autoPlay ? 1 : 0,
          playlist: isLooped ? youTubeVideoId : undefined
        },
        events: {
          onReady,
          onStateChange
        }
      });
      resolve();
    });
  };
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<Player | null>(null);

  // Unique ID for each iframe to avoid conflicts with multiple players
  const uniqueId = `youtube-iframe-${youTubeVideoId}`;
  const isHoveredStylesShowed = isVideoHovered || !isFirstPlayTriggered || !isPlaying;

  // Effect to set iframe src based on provided parameters
  useEffect(() => {
    const autoplayParam = autoPlay ? 1 : 0;
    const loopParam = isLooped ? 1 : 0;
    const muteParam = isMuted || autoPlay ? 1 : 0;
    const playlistParam = isLooped ? `&playlist=${youTubeVideoId}` : '';
    const src = `https://www.youtube.com/embed/${youTubeVideoId}?autoplay=${autoplayParam}&loop=${loopParam}&mute=${muteParam}${playlistParam}&rel=0&enablejsapi=1&controls=0`;
    setIframeSrc(src);
    if (iframeRef.current) {
      iframeRef.current.src = src;
    }
  }, [youTubeVideoId, autoPlay, isLooped, isMuted]);
  useEffect(() => {
    const currentPlayer = playerRef.current;
    const initialize = async () => {
      try {
        await loadYouTubeIframeAPI(youTubeVideoId);
        await buildYouTubePlayer({
          uniqueId,
          youTubeVideoId,
          autoPlay: !!autoPlay,
          isLooped: !!isLooped,
          isMuted: !!isMuted,
          onReady: onPlayerReady,
          onStateChange: handleStateChange,
          playerRef
        });
      } catch (error) {
        console.warn(error);
      }
    };

    // Call the initialize function
    void initialize();

    // Cleanup function to destroy the player
    return () => {
      if (currentPlayer) {
        currentPlayer.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [youTubeVideoId, autoPlay, isLooped, isMuted]);

  // Function called when the player is ready
  const onPlayerReady = (event: PlayerEvent) => {
    const playerId = event.target.getVideoData();
    if (playerId.video_id === youTubeVideoId) {
      setIsLoading(false);
    }
    if (autoPlay) {
      event.target.playVideo();
    }
  };

  // Function to handle state changes of the player
  const handleStateChange = (event: OnStateChangeEvent) => {
    if (event.data === globalThis.window?.YT?.PlayerState.ENDED) {
      setIsPlaying(false);
    }
  };

  // Function to handle play/pause actions
  const handlePlayPause = () => {
    const iframe = iframeRef.current;
    if (iframe) {
      const iframeWindow = iframe.contentWindow;
      const origin = new URL(iframe.src).origin;
      if (isPlaying) {
        iframeWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', origin);
        setIsPlaying(false);
      } else {
        iframeWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', origin);
        setIsPlaying(true);
      }
      setIsFirstPlayTriggered(true);
    }
  };
  const handleMuteUnmute = () => {
    const iframe = iframeRef.current;
    if (iframe) {
      const iframeWindow = iframe.contentWindow;
      const origin = new URL(iframe.src).origin;
      if (isNowMuted) {
        iframeWindow?.postMessage('{"event":"command","func":"unMute","args":""}', origin);
        setIsNowMuted(false);
      } else {
        iframeWindow?.postMessage('{"event":"command","func":"mute","args":""}', origin);
        setIsNowMuted(true);
      }
    }
  };
  if (!youTubeVideoId) {
    return <></>;
  }
  return <Box sx={ytVideoSectionWrapperStyles} data-testid="group_service_media_yt" onMouseOver={() => setIsVideoHovered(true)} onMouseOut={() => setIsVideoHovered(false)} onTouchStart={() => {
    setIsVideoHovered(true);
    setTimeout(() => {
      setIsVideoHovered(false);
    }, 1500);
  }} data-sentry-element="Box" data-sentry-component="YouTubeVideoEmbed" data-sentry-source-file="YouTubeVideoEmbed.tsx">
			{!isPlaying && youTubeVideoImage ? <Box sx={ytVideoInnerSectionStyles(isPlaying, isLoading)}>
					<ImageWrapper src={youTubeVideoImage?.url} alt={youTubeVideoImage?.description ?? ''} style={ytImageStyles(isBorderRadius)} />
				</Box> : null}
			{/* Aspect ratio container for small screen */}
			<Box data-testid={'youtube_aspect_ratio_for_mobile'} sx={youtubeMobileStyles(isExtraSmallScreen, fromHero, isBorderRadius, sixteenByNineVideoRatio)} data-sentry-element="Box" data-sentry-source-file="YouTubeVideoEmbed.tsx">
				<iframe data-testid={'youtube_embedded_iframe'} ref={iframeRef} id={uniqueId} src={iframeSrc} allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{
        ...youtubeIframeStyles(isExtraSmallScreen, isBorderRadius, sixteenByNineVideoRatio),
        border: 0
      }} title="YouTube Video" />
			</Box>
			<Box sx={{
      ...ytVideoControlStyles(),
      ...videoControlsSx,
      ...videoHoverEffect(isHoveredStylesShowed)
    }} data-sentry-element="Box" data-sentry-source-file="YouTubeVideoEmbed.tsx">
				<IconButton sx={videoIconButtonStyles} onClick={handlePlayPause} aria-label={isPlaying ? 'Pause' : 'Play'} data-sentry-element="IconButton" data-sentry-source-file="YouTubeVideoEmbed.tsx">
					{isPlaying ? <Pause data-testid={'pause_button'} sx={videoControlIconStyles} /> : <PlayArrow data-testid={'play_button'} sx={videoControlIconStyles} />}
				</IconButton>
				<IconButton sx={videoIconButtonStyles} onClick={handleMuteUnmute} aria-label={isNowMuted ? 'Unmute' : 'Mute'} data-sentry-element="IconButton" data-sentry-source-file="YouTubeVideoEmbed.tsx">
					{isNowMuted ? <VolumeOff data-testid={'volume_off_button'} sx={videoControlIconStyles} /> : <VolumeMute data-testid={'volume_on_button'} sx={videoControlIconStyles} />}
				</IconButton>
			</Box>
		</Box>;
}